import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  PenTool,
  AlertTriangle,
  Calendar,
  Eye,
  Loader2,
  Shield,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface PayrollDocument {
  id: string;
  employee_id: string;
  employee_name: string;
  period: string;
  title: string;
  file_url: string;
  file_name: string;
  file_size: number;
  status: "pending" | "signed" | "rejected";
  signed_at: string | null;
  signature_url: string | null;
  signed_from_ip: string | null;
  rejection_reason: string | null;
  notes: string | null;
  created_at: string;
}

export default function Payroll() {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [clientIp, setClientIp] = useState<string | null>(null);

  const [selectedDoc, setSelectedDoc] = useState<PayrollDocument | null>(null);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);

  // Fetch client IP on mount
  useEffect(() => {
    const fetchIp = async () => {
      try {
        // Try to get IP from our server first
        const response = await fetch('/api/client-ip');
        if (response.ok) {
          const data = await response.json();
          setClientIp(data.ip);
        } else {
          // Fallback to external service
          const extResponse = await fetch('https://api.ipify.org?format=json');
          if (extResponse.ok) {
            const data = await extResponse.json();
            setClientIp(data.ip);
          }
        }
      } catch (error) {
        console.log('Could not fetch client IP');
        setClientIp('No disponible');
      }
    };
    fetchIp();
  }, []);

  // Fetch user's payroll documents
  const { data: documents, isLoading } = useQuery({
    queryKey: ["payroll-documents", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await api
        .from("payroll_documents")
        .select("*")
        .eq("employee_id", user.id)
        .eq("is_active", true)
        .order("period", { ascending: false });
      if (error) throw error;
      return data as PayrollDocument[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  // Sign document mutation
  const signMutation = useMutation({
    mutationFn: async ({ docId, signature }: { docId: string; signature: string }) => {
      const { error } = await api
        .from("payroll_documents")
        .update({
          status: "signed",
          signed_at: new Date().toISOString(),
          signature_url: signature,
          signed_from_ip: clientIp || "No disponible",
          signed_user_agent: navigator.userAgent,
        })
        .eq("id", docId);
      if (error) throw error;

      // Mark notification as read
      await api
        .from("user_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("reference_type", "payroll")
        .eq("reference_id", docId);

      // Create notification for admin
      await api.from("user_notifications").insert([{
        user_id: null, // Will be handled by trigger or picked up by admins
        title: "Nómina firmada",
        message: `${profile?.full_name} ha firmado su nómina del período ${selectedDoc?.period}`,
        type: "success",
        category: "payroll",
        reference_type: "payroll",
        reference_id: docId,
      }]).catch(() => {}); // Don't fail if notification fails
    },
    onSuccess: () => {
      toast({ title: "Documento firmado", description: "La nómina ha sido firmada correctamente" });
      queryClient.invalidateQueries({ queryKey: ["payroll-documents"] });
      queryClient.invalidateQueries({ queryKey: ["user-notifications"] });
      setSignDialogOpen(false);
      setSelectedDoc(null);
      clearSignature();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Reject document mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ docId, reason }: { docId: string; reason: string }) => {
      const { error } = await api
        .from("payroll_documents")
        .update({
          status: "rejected",
          rejection_reason: reason,
        })
        .eq("id", docId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Documento rechazado", description: "Se ha registrado el rechazo" });
      queryClient.invalidateQueries({ queryKey: ["payroll-documents"] });
      setRejectDialogOpen(false);
      setSelectedDoc(null);
      setRejectionReason("");
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Canvas signature functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsDrawing(true);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = "touches" in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = "touches" in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL("image/png"));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignatureData(null);
  };

  const handleSign = () => {
    if (!selectedDoc || !signatureData) return;
    signMutation.mutate({ docId: selectedDoc.id, signature: signatureData });
  };

  const handleReject = () => {
    if (!selectedDoc || !rejectionReason.trim()) return;
    rejectMutation.mutate({ docId: selectedDoc.id, reason: rejectionReason });
  };

  const openSignDialog = (doc: PayrollDocument) => {
    setSelectedDoc(doc);
    setSignDialogOpen(true);
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
    }, 100);
  };

  const handlePreview = (doc: PayrollDocument) => {
    setPreviewError(false);
    setPreviewUrl(doc.file_url);
  };

  const handleDownload = async (doc: PayrollDocument) => {
    try {
      const response = await fetch(doc.file_url);
      if (!response.ok) throw new Error("Archivo no encontrado");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Error al descargar",
        description: "El archivo no está disponible.",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Firmado</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
      default:
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    }
  };

  const pendingDocs = documents?.filter(d => d.status === "pending") || [];
  const signedDocs = documents?.filter(d => d.status === "signed") || [];
  const rejectedDocs = documents?.filter(d => d.status === "rejected") || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Mis Nóminas</h1>
          <p className="text-muted-foreground mt-1">
            Visualiza y firma tus documentos de nómina
          </p>
        </div>

        {/* Pending Alert */}
        {pendingDocs.length > 0 && (
          <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-800 dark:text-yellow-200">
                  Tienes {pendingDocs.length} nómina(s) pendiente(s) de firma
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Por favor revisa y firma los documentos para confirmar su recepción
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{pendingDocs.length}</p>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{signedDocs.length}</p>
                  <p className="text-sm text-muted-foreground">Firmadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{rejectedDocs.length}</p>
                  <p className="text-sm text-muted-foreground">Rechazadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Documents List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : documents && documents.length > 0 ? (
          <div className="space-y-4">
            {documents.map((doc) => (
              <Card
                key={doc.id}
                className={cn(
                  "transition-all",
                  doc.status === "pending" && "border-l-4 border-l-yellow-500",
                  doc.status === "signed" && "border-l-4 border-l-green-500"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{doc.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Calendar className="w-4 h-4" />
                          <span>Período: {doc.period}</span>
                        </div>
                        {doc.signed_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Firmado el {new Date(doc.signed_at).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        )}
                        {doc.rejection_reason && (
                          <p className="text-xs text-red-600 mt-1">
                            Motivo: {doc.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      {getStatusBadge(doc.status)}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreview(doc)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Descargar
                      </Button>

                      {doc.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => openSignDialog(doc)}
                          >
                            <PenTool className="w-4 h-4 mr-1" />
                            Firmar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setRejectDialogOpen(true);
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Signature badge for signed documents */}
                  {doc.status === "signed" && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                        <Shield className="w-4 h-4" />
                        <span className="font-medium">Documento firmado electrónicamente</span>
                        <span className="text-muted-foreground">
                          - {new Date(doc.signed_at!).toLocaleString("es-ES")}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No tienes nóminas registradas</p>
            </CardContent>
          </Card>
        )}

        {/* Sign Dialog */}
        <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Firmar Nómina</DialogTitle>
              <DialogDescription>
                Dibuja tu firma para confirmar la recepción del documento
              </DialogDescription>
            </DialogHeader>

            {selectedDoc && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium">{selectedDoc.title}</p>
                  <p className="text-sm text-muted-foreground">Período: {selectedDoc.period}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Tu firma:</p>
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={clearSignature}>
                    Limpiar firma
                  </Button>
                </div>

                {/* Electronic signature notice */}
                <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-800 dark:text-blue-300">
                      <p className="font-medium mb-1">Firma Electrónica</p>
                      <p>
                        Al firmar este documento, confirmas que has recibido y revisado tu nómina.
                        Se registrarán los siguientes datos como evidencia de la firma:
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        <li>• Fecha y hora: {new Date().toLocaleString("es-ES")}</li>
                        <li>• ID Empleado: {profile?.employee_id || user?.id?.substring(0, 8)}</li>
                        <li>• IP: {clientIp || "Obteniendo..."}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setSignDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSign}
                disabled={!signatureData || signMutation.isPending}
              >
                {signMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Shield className="w-4 h-4 mr-2" />
                Confirmar Firma
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Rechazar Nómina</AlertDialogTitle>
              <AlertDialogDescription>
                Por favor indica el motivo del rechazo. Esta acción notificará al departamento de recursos humanos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              placeholder="Escribe el motivo del rechazo..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReject}
                disabled={!rejectionReason.trim() || rejectMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {rejectMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Rechazar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Preview Dialog */}
        <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Vista previa del documento</DialogTitle>
            </DialogHeader>
            {previewUrl && !previewError ? (
              <iframe
                src={previewUrl}
                className="w-full h-[70vh] border rounded"
                title="Document Preview"
                onError={() => setPreviewError(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-[40vh] text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mb-4 text-yellow-500" />
                <p className="font-medium">No se pudo cargar el documento</p>
                <p className="text-sm mt-1">El archivo puede no estar disponible.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setPreviewUrl(null)}
                >
                  Cerrar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
